import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const newKey = () => `vk_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
const fmt = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

// Song Versions — Save Version + Version History + Restore as new copy.
export const StudioVersionsPanel = ({ projectId, onRestored }) => {
  const [versions, setVersions] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const keyRef = useRef(newKey());

  const load = useCallback(() => {
    api.get(`/studio/projects/${projectId}/versions`)
      .then((r) => setVersions(r.data.versions || []))
      .catch(() => setVersions([]));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/studio/projects/${projectId}/versions`, { name, note, client_key: keyRef.current });
      keyRef.current = newKey();
      setName(""); setNote("");
      toast.success("Version saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save version");
    } finally { setBusy(false); }
  };

  const restore = async (v) => {
    if (!window.confirm(`Restore "${v.name}" as a new project copy? Your current project stays as it is.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/studio/projects/${projectId}/versions/${v.version_id}/restore`, {});
      toast.success(`Restored as "${r.data.title}"`);
      onRestored?.(r.data.project_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore version");
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-10" data-testid="studio-versions-panel">
      <h2 className="text-sm font-bold text-gray-300 mb-3">Song Versions</h2>
      <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10 space-y-2">
        <input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Version name (e.g. Final mix)"
          className="w-full h-10 px-3 rounded-lg bg-black/40 border border-white/10 text-sm text-white placeholder:text-gray-500"
          data-testid="version-name-input" />
        <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
          className="w-full h-10 px-3 rounded-lg bg-black/40 border border-white/10 text-sm text-white placeholder:text-gray-500"
          data-testid="version-note-input" />
        <button type="button" onClick={save} disabled={busy}
          className="w-full h-10 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm font-black disabled:opacity-50"
          data-testid="version-save-btn">{busy ? "Working…" : "Save Version"}</button>
        <p className="text-[10px] text-gray-500">Saves beat, takes, edits, mix and mastering. Saved versions are never overwritten.</p>
      </div>
      <h3 className="text-xs font-bold text-gray-400 mt-5 mb-2">Version History</h3>
      {versions === null && <p className="text-xs text-gray-500">Loading…</p>}
      {versions && versions.length === 0 && <p className="text-xs text-gray-500" data-testid="version-empty">No saved versions yet.</p>}
      <ul className="space-y-2" data-testid="version-list">
        {(versions || []).map((v) => (
          <li key={v.version_id} className="p-3 rounded-xl bg-white/[0.03] border border-white/10 flex items-start gap-3" data-testid={`version-${v.number}`}>
            <span className="shrink-0 w-9 h-9 rounded-lg bg-amber-400/15 text-amber-200 text-xs font-black flex items-center justify-center">v{v.number}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{v.name}</p>
              <p className="text-[10px] text-gray-500">{fmt(v.created_at)} · {v.summary.takes} {v.summary.takes === 1 ? "take" : "takes"}{v.summary.master?.preset && v.summary.master.preset !== "off" ? ` · ${v.summary.master.preset}` : ""}</p>
              {v.note && <p className="text-xs text-gray-400 mt-1 break-words">{v.note}</p>}
            </div>
            <button type="button" onClick={() => restore(v)} disabled={busy}
              className="shrink-0 h-8 px-3 rounded-full border border-white/15 text-[11px] font-bold text-gray-200 hover:border-amber-300/50"
              data-testid={`version-restore-${v.number}`}>Restore as copy</button>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default StudioVersionsPanel;
